import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { apiClient } from './client';

// Types
export interface AdminOverview {
  totalUsers: number;
  totalVendors: number;
  properties: {
    total: number;
    verified: number;
    pending: number;
    disabled: number;
  };
  bookings: {
    total: number;
    byStatus: Record<string, number>;
  };
  totalRevenue: number;
  revenue?: number;
  series: Array<{
    month: string;
    label: string;
    users: number;
    bookings: number;
    revenue: number;
  }>;
  payoutLiability: {
    pendingPayoutsTotal: number;
    lockedBalancesTotal: number;
  };
  refundsTotal: number;
  cancellationRate: number;
  bookingsByStatus: Record<string, number>;
  topProperties: Array<{
    id: string;
    name: string;
    total: number;
  }>;
}

export interface PaginationResponse<T> {
  data: T[];
  page: number;
  limit: number;
  totalPage: number;
}

// User Types
export interface User {
  id: string;
  fullName: string;
  email: string;
  role: string;
  deactivated: boolean;
  createdAt: string;
  _count: {
    bookings: number;
    locations: number;
  };
}

export interface FetchUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  deactivated?: string;
}

export interface Vendor {
  id: string;
  fullName: string;
  email: string;
  deactivated: boolean;
  createdAt: string;
  wallet?: {
    balance: number;
    ledgerBalance: number;
  };
  _count: {
    locations: number;
  };
}

export interface Property {
  id: string;
  name: string;
  address: string;
  price: number;
  isVerified: boolean;
  isDisabled: boolean;
  isPromoted: boolean;
  vendor: {
    fullName: string;
    email: string;
  };
  createdAt: string;
}

export interface FetchPropertiesParams {
  page?: number;
  limit?: number;
  search?: string;
  isVerified?: string;
  isDisabled?: string;
  isPromoted?: string;
  vendorId?: string;
}

export interface Booking {
  id: string;
  status: string;
  fromDate: string;
  toDate: string;
  createdAt: string;
  user: { fullName: string; email: string };
  location: { name: string; address: string };
  transaction: { id: string; status: string; amount: number; tag: string };
}

export interface Transaction {
  id: string;
  amount: number;
  status: string;
  tag: string;
  type: string;
  createdAt: string;
  user: { fullName: string; email: string };
}

export interface Payout {
  id: string;
  amount: number;
  status: string;
  createdAt: string;
  vendor: { fullName: string; email: string };
}

export interface Review {
  id: string;
  rating: number;
  comment: string;
  createdAt: string;
  user: { fullName: string };
  location: { name: string };
}

export interface Setting {
  id: string;
  key: string;
  value: string;
}

export interface AuditEvent {
  id: string;
  userId?: string | null;
  event: string;
  ip?: string | null;
  userAgent?: string | null;
  metadata?: unknown;
  createdAt: string;
  user?: { id: string; fullName: string; email: string } | null;
}

export interface PageParams {
  page?: number;
  limit?: number;
}

export interface FetchBookingsParams extends PageParams {
  status?: string;
  locationId?: string;
  userId?: string;
  from?: string;
  to?: string;
}

export interface FetchPayoutsParams extends PageParams {
  status?: string;
}

export interface FetchReviewsParams extends PageParams {
  locationId?: string;
  rating?: number | string;
}

export interface FetchAuditEventsParams extends PageParams {
  userId?: string;
  event?: string;
  from?: string;
  to?: string;
}

// Queries
export function useAdminOverview(): UseQueryResult<AdminOverview, Error> {
  return useQuery(['admin-overview'], async () => {
    const { data } = await apiClient.get<AdminOverview>('/admin/overview');
    return data;
  });
}

export function useAdminUsers(params: FetchUsersParams): UseQueryResult<PaginationResponse<User>, Error> {
  return useQuery(['admin-users', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<User>>('/admin/users', { params });
    return data;
  });
}

export function useAdminVendors(params: FetchUsersParams): UseQueryResult<PaginationResponse<Vendor>, Error> {
  return useQuery(['admin-vendors', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<Vendor>>('/admin/vendors', { params });
    return data;
  });
}

export function useAdminProperties(params: FetchPropertiesParams): UseQueryResult<PaginationResponse<Property>, Error> {
  return useQuery(['admin-properties', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<Property>>('/admin/locations', { params });
    return data;
  });
}

export function useAdminBookings(params: FetchBookingsParams): UseQueryResult<PaginationResponse<Booking>, Error> {
  return useQuery(['admin-bookings', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<Booking>>('/admin/bookings', { params });
    return data;
  });
}

export function useAdminTransactions(params: PageParams): UseQueryResult<PaginationResponse<Transaction>, Error> {
  return useQuery(['admin-transactions', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<Transaction>>('/admin/transactions', { params });
    return data;
  });
}

export function useAdminPayouts(params: FetchPayoutsParams): UseQueryResult<PaginationResponse<Payout>, Error> {
  return useQuery(['admin-payouts', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<Payout>>('/admin/payouts', { params });
    return data;
  });
}

export function useAdminReviews(params: FetchReviewsParams): UseQueryResult<PaginationResponse<Review>, Error> {
  return useQuery(['admin-reviews', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<Review>>('/admin/reviews', { params });
    return data;
  });
}

export function useAdminAuditEvents(params: FetchAuditEventsParams): UseQueryResult<PaginationResponse<AuditEvent>, Error> {
  return useQuery(['admin-audit-events', params], async () => {
    const { data } = await apiClient.get<PaginationResponse<AuditEvent>>('/admin/audit-events', { params });
    return data;
  });
}

export function useAdminSettings(): UseQueryResult<Setting[], Error> {
  return useQuery(['admin-settings'], async () => {
    const { data } = await apiClient.get<Setting[]>('/settings/setting/fetch');
    return data;
  });
}

// Mutations
export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation(
    async (userId: string) => apiClient.patch(`/admin/users/${userId}/deactivate`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-users']);
        queryClient.invalidateQueries(['admin-vendors']);
      },
    }
  );
}

export function useReactivateUser() {
  const queryClient = useQueryClient();
  return useMutation(
    async (userId: string) => apiClient.patch(`/admin/users/${userId}/reactivate`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-users']);
        queryClient.invalidateQueries(['admin-vendors']);
      },
    }
  );
}

export function useVerifyUser() {
  const queryClient = useQueryClient();
  return useMutation(
    async (userId: string) => apiClient.patch(`/admin/users/${userId}/verify`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-users']);
        queryClient.invalidateQueries(['admin-vendors']);
      },
    }
  );
}

export function useVerifyLocation() {
  const queryClient = useQueryClient();
  return useMutation(
    async (locationId: string) => apiClient.patch(`/admin/locations/${locationId}/verify`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-properties']),
    }
  );
}

export function useUnverifyLocation() {
  const queryClient = useQueryClient();
  return useMutation(
    async (locationId: string) => apiClient.patch(`/admin/locations/${locationId}/unverify`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-properties']),
    }
  );
}

export function useDisableLocation() {
  const queryClient = useQueryClient();
  return useMutation(
    async (locationId: string) => apiClient.patch(`/admin/locations/${locationId}/disable`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-properties']),
    }
  );
}

export function useEnableLocation() {
  const queryClient = useQueryClient();
  return useMutation(
    async (locationId: string) => apiClient.patch(`/admin/locations/${locationId}/enable`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-properties']),
    }
  );
}

export function usePromoteLocation() {
  const queryClient = useQueryClient();
  return useMutation(
    async (locationId: string) => apiClient.patch(`/admin/locations/${locationId}/promote`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-properties']),
    }
  );
}

export function useUnpromoteLocation() {
  const queryClient = useQueryClient();
  return useMutation(
    async (locationId: string) => apiClient.patch(`/admin/locations/${locationId}/unpromote`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-properties']),
    }
  );
}

export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, status }: { id: string; status: string }) => apiClient.patch(`/admin/bookings/${id}/status`, { status }),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-bookings']),
    }
  );
}

export function useApprovePayout() {
  const queryClient = useQueryClient();
  return useMutation(
    async (payoutId: string) => apiClient.patch(`/admin/payouts/${payoutId}/approve`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-payouts']),
    }
  );
}

export function useRejectPayout() {
  const queryClient = useQueryClient();
  return useMutation(
    async (payoutId: string) => apiClient.patch(`/admin/payouts/${payoutId}/reject`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-payouts']),
    }
  );
}

export function useMarkPayoutPaid() {
  const queryClient = useQueryClient();
  return useMutation(
    async (payoutId: string) => apiClient.patch(`/admin/payouts/${payoutId}/paid`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-payouts']),
    }
  );
}

export function useDeleteReview() {
  const queryClient = useQueryClient();
  return useMutation(
    async (reviewId: string) => apiClient.delete(`/admin/reviews/${reviewId}`),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-reviews']),
    }
  );
}

export function useCreateSetting() {
  const queryClient = useQueryClient();
  return useMutation(
    async (data: { key: string; value: string }) => apiClient.post('/settings/setting/create', data),
    {
      onSuccess: () => queryClient.invalidateQueries(['admin-settings']),
    }
  );
}

export function useSendAnnouncement() {
  return useMutation(
    async (data: { title: string; body: string; audience: string; channels: string[] }) =>
      apiClient.post('/admin/announcements', data)
  );
}
